/**
 * Showroom Reception Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as showroomService from '../api/services/showroom.service';
import type { ShowroomImportRequest, ShowroomConfirmRequest } from '../types';
import { toast } from '../stores/toast.store';

const KEYS = {
  records: ['showroom', 'records'] as const,
  record: (id: number) => ['showroom', 'record', id] as const,
};

/**
 * Hook for fetching recent showroom records
 */
export function useShowroomRecords(limit?: number) {
  return useQuery({
    queryKey: [...KEYS.records, limit],
    queryFn: async () => {
      const result = await showroomService.getShowroomRecords(limit);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch records');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for OCR scanning showroom reception form (does not write to DB)
 */
export function useImportShowroom() {
  return useMutation({
    mutationFn: async (data: ShowroomImportRequest) => {
      const result = await showroomService.importShowroom(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to scan form');
      }
      return result.data!;
    },
    onError: (error: Error) => {
      toast.error('辨識失敗', error.message);
    },
  });
}

/**
 * Hook for confirming OCR result and writing to DB
 */
export function useConfirmShowroom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ShowroomConfirmRequest) => {
      const result = await showroomService.confirmShowroom(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to save record');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.records });
      toast.success('儲存成功', `接待記錄 ${data.name} 已建立`);
    },
    onError: (error: Error) => {
      toast.error('儲存失敗', error.message);
    },
  });
}
